// jl-design-system
import stripWhitespace from 'jl-design-system/utils/string/stripWhitespace';
import env from 'jl-design-system/utils/env/env';
import { getFormInitialValuesFromAddress } from 'jl-design-system/utils/address/address';
import { phoneNumberUkMobileRegex } from 'jl-design-system/form/regex';
import transformUkPhoneNumber from 'jl-design-system/form/normalizers/transformUkPhoneNumber';
import { TITLE_OPTIONS } from 'jl-design-system/form/fields/title';

// lodash
import includes from 'lodash/includes';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import isEqual from 'lodash/isEqual';
import isArray from 'lodash/isArray';
import some from 'lodash/some';
import get from 'lodash/get';
import merge from 'lodash/merge';
import initial from 'lodash/initial';
import last from 'lodash/last';
import tail from 'lodash/tail';

import appConstants from '../../constants/appConstants';
import featureConstants from '../../constants/featureConstants';
import paymentTypeConstants from '../../constants/paymentTypeConstants';
import { BILLING_ADDRESS_FORM_ID, RESIDENTIAL_ADDRESS_FORM_ID } from '../form/configs/billingAddress';
import { PAYPAL_BILLING_ADDRESS_FORM_ID } from '../form/configs/payPalBillingAddress';
import { APPLE_PAY_BILLING_ADDRESS_FORM_ID } from '../form/configs/applePayBillingAddress';
import { GOOGLE_PAY_BILLING_ADDRESS_FORM_ID } from '../form/configs/googlePayBillingAddress';
import { isFormMounted } from '..';
import { getStorageItem } from '../storage/storage';
import mergeBillingAddressSources from './mergeBillingAddressSources';

export function shouldMockLoqate() {
  return getStorageItem(featureConstants.USE_REAL_LOQATE_OVERRIDE, true) === 'true' ?
    false : env.isClientLocal || env.isClientDev;
}

export function getAddressFromFormValues(formValues = {}) {
  const {
    title,
    firstName,
    lastName,
    addressLine1,
    addressLine2,
    addressLine3,
    addressLine4,
    townOrCity,
    countyStateOrProvince,
    postcode,
    countryCode,
    phoneNumber,
  } = formValues;

  // TODO conditionally add fields to objects to avoid creating object full of undefined values
  return {
    addressee: {
      // small hack to conditionally set title as field is not required/used in invalid paypal billing form
      ...(title && { title }),
      firstName,
      lastName,
    },
    address: {
      addressLine1,
      addressLine2,
      ...Object.keys({
        addressLine3,
        addressLine4,
      }).reduce((acc, key) => (formValues[key] ? { ...acc, [key]: formValues[key] } : acc), {}),
      townOrCity,
      countyStateOrProvince,
      postcode,
      countryCode,
    },
    phoneNumber: stripWhitespace(transformUkPhoneNumber(phoneNumber)),
  };
}

export function parseAddressPhoneNumber(addressObject) {

  if (addressObject) {
    const { phoneNumber } = addressObject;
    return {
      ...addressObject,
      ...(phoneNumber ? { phoneNumber: stripWhitespace(transformUkPhoneNumber(phoneNumber)) } : {}),
    };
  }

  return addressObject;
}

export function getDeliveryAddressPayloadFromFormValues(formValues = {}) {
  const {
    id,
  } = formValues;

  const addressDetails = {
    ...getAddressFromFormValues(formValues),
  };

  if (id) {
    addressDetails.id = id;
  }

  return {
    ...addressDetails,
  };
}

export function getAddressBookAddressPayloadFromFormValues(formValues = {}) {
  const {
    id,
    contact,
  } = formValues;

  const payload = {
    ...getAddressFromFormValues(formValues),
    contact: contact || false,
  };

  if (id) {
    payload.id = id;
  }

  return payload;
}

export function getBillingAddressForPaymentType({ addressBook, paymentType = '', savedPaymentCards, shouldSetBillingAddress = true }) {
  if (!shouldSetBillingAddress) {
    return null;
  }

  if (includes(paymentType, paymentTypeConstants.SAVED_CREDIT_CARD)) {
    const selectedSavedCardId = paymentType.split(':')[1];
    const selectedCard = find(savedPaymentCards, { id: selectedSavedCardId });
    const paymentCardAddressInAddressBook = find(addressBook, { id: selectedCard.addressId });

    if (paymentCardAddressInAddressBook) {
      // MARV-8816 only return the saved payment card's address if it can be found in address book
      return paymentCardAddressInAddressBook;
    }
  }

  const contactAddress = find(addressBook, { contact: true, address: { countryCode: 'GB' } });
  if (contactAddress) {
    return contactAddress;
  }

  return addressBook[0];
}

export function getBillingAddress(state = {}) {

  const isPosCreditPayment = includes(get(state, 'payment.selectedPaymentType'), paymentTypeConstants.POS_CREDIT);

  if (isPosCreditPayment) {
    if (isFormMounted(get(state.form, RESIDENTIAL_ADDRESS_FORM_ID))) {
      const billingAddressFormValues = get(state, `form[${RESIDENTIAL_ADDRESS_FORM_ID}].values`);
      return getAddressFromFormValues(billingAddressFormValues);
    }

    return parseAddressPhoneNumber(get(state, 'user.selectedResidentialAddress'));
  }

  if (isFormMounted(get(state.form, PAYPAL_BILLING_ADDRESS_FORM_ID))) {
    const billingAddressFormValues = get(state, `form[${PAYPAL_BILLING_ADDRESS_FORM_ID}].values`);
    return getAddressFromFormValues(billingAddressFormValues);
  }

  if (isFormMounted(get(state.form, GOOGLE_PAY_BILLING_ADDRESS_FORM_ID))) {
    const billingAddressFormValues = get(state, `form[${GOOGLE_PAY_BILLING_ADDRESS_FORM_ID}].values`);
    return getAddressFromFormValues(billingAddressFormValues);
  }

  if (isFormMounted(get(state.form, APPLE_PAY_BILLING_ADDRESS_FORM_ID))) {
    const billingAddressFormValues = get(state, `form[${APPLE_PAY_BILLING_ADDRESS_FORM_ID}].values`);
    return getAddressFromFormValues(billingAddressFormValues);
  }

  if (isFormMounted(get(state.form, BILLING_ADDRESS_FORM_ID))) {
    const mergedValues = mergeBillingAddressSources(state, true);
    return getAddressFromFormValues(mergedValues);
  }

  if (get(state, 'user.previewSelectedBillingAddress')) {
    const mergedValues = mergeBillingAddressSources(state);
    return getAddressFromFormValues(mergedValues);
  }

  return parseAddressPhoneNumber(
    get(state, 'user.selectedBillingAddress') ||
    get(state, 'delivery.confirmedDeliveryAddress'),
  );
}

export function checkAddressFormValuesEqualToAddressObject(formValues, address) {

  const parsedFormValues = Object.keys(formValues).reduce((acc, key) => (formValues[key] ? {
    ...acc,
    [key]: formValues[key],
  } : acc), {});

  const addressValues = getFormInitialValuesFromAddress(address);
  const parsedAddressValues = Object.keys(addressValues).reduce((acc, key) => (addressValues[key] ? {
    ...acc,
    [key]: addressValues[key],
  } : acc), {});

  return isEqual(parsedFormValues, parsedAddressValues);
}

export function getPhoneNumberDropdownOptions() {
  const countries = JSON.parse(getStorageItem(appConstants.COUNTRIES));

  const validCountries = isArray(countries) ? countries : [];
  const formattedCountries = validCountries.map(country => ({
    callingCode: country.callingCode,
    label: `${country.label} (${country.callingCode})`,
    selectedLabel: `(${country.callingCode})`,
    placeholder: country.callingCodePlaceholder,
    value: country.value,
  }));

  // move GB to top of the list
  const gbIndex = findIndex(formattedCountries, { value: 'GB' });
  formattedCountries.splice(0, 0, formattedCountries[gbIndex]);
  formattedCountries.splice(gbIndex + 1, 1);

  return formattedCountries;
}

export function checkIsUkMobileNumber(number) {
  const regex = phoneNumberUkMobileRegex;
  return regex.test(number);
}

export function countryCodeIsInternational(countryCode) {
  const britishCountryCodes = ['GB', 'GG', 'IM', 'JE'];
  return !some(britishCountryCodes, code => includes(code, countryCode));
}

export function getFormattedAddressFromApplePay(applePayAddress, phoneNumber) {
  const addressLines = get(applePayAddress, 'addressLines', []);
  const {
    administrativeArea,
    countryCode,
    familyName,
    givenName,
    locality,
    postalCode,
  } = applePayAddress;

  return {
    addressee: {
      firstName: givenName,
      lastName: familyName,
    },
    address: {
      ...(merge({}, ...addressLines.map((item, index) => ({ [`addressLine${index + 1}`]: item })))),
      townOrCity: locality,
      countyStateOrProvince: administrativeArea,
      postcode: postalCode,
      countryCode,
    },
    phoneNumber: stripWhitespace(transformUkPhoneNumber(phoneNumber)),
  };
}

export function mapAddressValidationErrorToApplePayErrors(validationErrors = {}) {
  const mappedErrors = Object.keys(validationErrors).map((error) => {
    switch (error) {
      case 'firstName':
      case 'lastName': {
        return {
          field: 'name',
          readableFieldName: 'Name',
        };
      }

      case 'addressLine1':
      case 'addressLine2':
      case 'addressLine3':
      case 'addressLine4': {
        return {
          field: 'addressLines',
          readableFieldName: 'Address line(s)',
        };
      }

      case 'townOrCity':
        return {
          field: 'locality',
          readableFieldName: 'Town/City',
        };

      case 'countyStateOrProvince':
        return {
          field: 'administrativeArea',
          readableFieldName: 'County/State',
        };

      case 'postcode':
        return {
          field: 'postalCode',
          readableFieldName: 'Post code',
        };

      case 'phoneNumber': {
        return {
          field: 'phoneNumber',
          key: 'shippingContactInvalid',
          readableFieldName: 'Phone number',
        };
      }
      default:
        return undefined;
    }
  });

  let errorMessage;
  if (Object.keys(validationErrors).length === 1) {
    errorMessage = `There is an issue with your ${mappedErrors[0]?.readableFieldName}, please update it to continue.`;
  } else {
    errorMessage = 'There are issues with your address, please update them to continue.';
  }

  return {
    paymentMethod: mappedErrors.map(error => new window.ApplePayError(error?.key || 'billingContactInvalid', error?.field, errorMessage)),
  };
}

export function mapGooglePayName(rawName) {
  const splitName = rawName.split(' ');

  // if first element matches a title field, separate from the rest of the name
  const titles = TITLE_OPTIONS.map(item => item.value);
  const titleExists = includes(titles, splitName[0]);
  const name = titleExists ? tail(splitName) : splitName;

  if (name.length === 2) {
    return {
      ...(titleExists ? { title: splitName[0] } : {}),
      firstName: name[0],
      lastName: name[1],
    };
  }

  if (name.length > 2) {
    return {
      ...(titleExists ? { title: splitName[0] } : {}),
      firstName: initial(name).join(' '),
      lastName: last(name),
    };
  }

  return {
    ...(titleExists ? { title: splitName[0] } : {}),
    firstName: name[0],
    lastName: '',
  };
}

export function getFormattedAddressFromGooglePay(googlePayAddress) {
  const {
    name,
    address1,
    address2,
    address3,
    locality,
    administrativeArea,
    postalCode,
    countryCode,
    phoneNumber,
  } = googlePayAddress;

  return {
    addressee: {
      ...mapGooglePayName(name),
    },
    address: {
      addressLine1: address1,
      addressLine2: address2,
      addressLine3: address3,
      countyStateOrProvince: administrativeArea,
      townOrCity: locality,
      postcode: postalCode,
      countryCode,
    },
    phoneNumber: stripWhitespace(transformUkPhoneNumber(phoneNumber)),
  };
}
